import { useState, type ReactNode } from "react";
import type { ClassifiedLine } from "../types";

export const ContextExpander = ({
  lines,
  onRenderLine,
  onRenderLines,
}: {
  lines: ClassifiedLine[];
  onRenderLine?: (line: ClassifiedLine) => ReactNode;
  onRenderLines?: (lines: ClassifiedLine[]) => ReactNode;
}) => {
  const [expanded, setExpanded] = useState(false);
  const count = lines.length;

  if (count === 0) return null;

  const label = `${count} line${count !== 1 ? "s" : ""}`;

  const dividerButton = (action: "expand" | "collapse") => (
    <button
      onClick={() => setExpanded(action === "expand")}
      className="w-full group flex items-center gap-3 py-2.5 text-xs text-zinc-400 hover:text-zinc-500 transition-colors cursor-pointer"
    >
      <span className="flex-1 border-t border-dashed border-zinc-200 group-hover:border-zinc-300 transition-colors" />
      <span className="shrink-0 flex items-center gap-1.5 select-none">
        {action === "expand" ? (
          <>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-50">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Show {label}
          </>
        ) : (
          <>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-50">
              <path d="M3 7.5L6 4.5L9 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Hide {label}
          </>
        )}
      </span>
      <span className="flex-1 border-t border-dashed border-zinc-200 group-hover:border-zinc-300 transition-colors" />
    </button>
  );

  if (!expanded) {
    return <div className="my-1">{dividerButton("expand")}</div>;
  }

  const content = onRenderLines
    ? onRenderLines(lines)
    : lines.map((line) => (
        <div key={line.num}>{onRenderLine!(line)}</div>
      ));

  return (
    <div className="my-1">
      {dividerButton("collapse")}
      <div className="border-l-2 border-zinc-100 pl-5 -ml-1 py-1 opacity-80">
        {content}
      </div>
      {count > 5 && dividerButton("collapse")}
    </div>
  );
};
